import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import api, { getBackendUrl } from '../../config/api';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BoltIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { useLiveBoatData } from '../../hooks/useLiveBoatData';
import { resolveLiveBoatZones } from '../../config/liveBoatZones';
import { resolveVesselImageUrl, resolveVrmEmbedUrl } from '../../utils/liveBoatUtils';
import { statusMeta } from '../../utils/propulsionAlarmUtils';
import BoatCanvas from './BoatCanvas';
import SystemTabs from './SystemTabs';
import ZoneDetailPanel from './ZoneDetailPanel';
import {
  Page,
  Header,
  Title,
  Subtitle,
  Toolbar,
  Badge,
  Btn,
} from './liveBoatStyles';

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const formatMode = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const LiveBoatView = () => {
  const { vesselId: vesselIdParam } = useParams();
  const navigate = useNavigate();
  const vesselId = parseInt(vesselIdParam, 10);

  const [vessel, setVessel] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState('electrical');

  const { roles, scene, modes, houseSoc, showEngines, showEngineZone, engineAlarms, vesselStreamConnected, refresh } =
    useLiveBoatData(vesselId);

  useEffect(() => {
    if (!vesselId) return;
    api.get(`/api/vessels/${vesselId}`).then((r) => setVessel(r.data));
  }, [vesselId]);

  const zones = useMemo(() => {
    const resolved = resolveLiveBoatZones(vessel?.monitoring_config);
    return resolved.filter((zone) => {
      if (!zone.hideWhenEmpty) return true;
      if (zone.id === 'engines') return showEngineZone;
      return true;
    });
  }, [vessel?.monitoring_config, showEngineZone]);

  useEffect(() => {
    if (!zones.some((z) => z.id === selectedZoneId) && zones.length > 0) {
      setSelectedZoneId(zones[0].id);
    }
  }, [zones, selectedZoneId]);

  const selectedZone = zones.find((z) => z.id === selectedZoneId) || zones[0] || null;
  const imageUrl = resolveVesselImageUrl(vessel?.primary_image_url, getBackendUrl());
  const vrmEmbedUrl = resolveVrmEmbedUrl(vessel?.monitoring_config, vesselId);
  const nav = roles.navigation?.fields;
  const inverterMode = formatMode(modes.inverter);
  const solarMode = formatMode(modes.solar);
  const engineAlarmMeta =
    engineAlarms?.status === 'alert' || engineAlarms?.status === 'warning' || engineAlarms?.status === 'stale'
      ? statusMeta(engineAlarms.status)
      : null;

  if (!vesselId) {
    return (
      <Page>
        <p>Invalid vessel.</p>
        <Link to="/vessels" style={{ color: theme.colors.secondary.light }}>
          Back to vessels
        </Link>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <div>
          <Btn type="button" onClick={() => navigate(-1)} style={{ marginBottom: '0.5rem' }}>
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </Btn>
          <Title>
            <BoltIcon className="w-6 h-6" style={{ color: theme.colors.secondary.light }} />
            Live Boat
          </Title>
          <Subtitle>
            Systems overview for {vessel?.vessel_name || 'your vessel'} — tap a system below to
            drill in.
          </Subtitle>
        </div>
        <Toolbar>
          <Badge
            $bg={vesselStreamConnected ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.12)'}
            $color={vesselStreamConnected ? theme.colors.success.light : theme.colors.error.light}
            $border={vesselStreamConnected ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)'}
          >
            <SignalIcon className="w-3.5 h-3.5" />
            {vesselStreamConnected ? 'Live' : 'Offline'}
          </Badge>
          {inverterMode ? <Badge>{inverterMode}</Badge> : null}
          {solarMode ? <Badge>{solarMode}</Badge> : null}
          {engineAlarmMeta ? (
            <Badge
              $bg={engineAlarmMeta.bg}
              $color={engineAlarmMeta.color}
              $border={`${engineAlarmMeta.color}55`}
            >
              Engine {engineAlarmMeta.label}
            </Badge>
          ) : null}
          <Btn type="button" onClick={() => refresh()}>
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Btn>
          <Link
            to={`/vessels/${vesselId}/power`}
            style={{ fontSize: theme.fontSizes.sm, color: theme.colors.secondary.light }}
          >
            Power flow →
          </Link>
          <Link
            to={`/vessels/${vesselId}/signalk/explore`}
            style={{ fontSize: theme.fontSizes.sm, color: theme.colors.secondary.light }}
          >
            All paths →
          </Link>
        </Toolbar>
      </Header>

      <Main>
        <BoatCanvas
          imageUrl={imageUrl}
          vesselName={vessel?.vessel_name}
          scene={scene}
          navFields={nav}
          houseSoc={houseSoc}
          roles={roles}
        />

        <SystemTabs
          zones={zones}
          roles={roles}
          houseSoc={houseSoc}
          engineAlarms={engineAlarms}
          selectedZoneId={selectedZoneId}
          onSelectZone={setSelectedZoneId}
        />

        <ZoneDetailPanel
          zone={selectedZone}
          roles={roles}
          vesselId={vesselId}
          houseSoc={houseSoc}
          vrmEmbedUrl={vrmEmbedUrl}
          engineAlarms={engineAlarms}
        />
      </Main>
    </Page>
  );
};

export default LiveBoatView;
